import { create } from "zustand";

/**
 * Grooming toasts (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1399): short
 * confirmations on the grooming pages, in SpecTree Toolbox and in a browser.
 * At most {@link GROOMING_TOAST_LIMIT} show at once (the oldest is dropped)
 * and each disappears after {@link GROOMING_TOAST_MS}. Rendered by
 * `GroomingToasts`; callable from plain functions such as `notifyBriefsReady`.
 *
 * A toast may carry one action (PKG-PM-C, feature PI-SPEC-SW-1490: "Carried
 * over 3 items · Undo", frame C2), the Toolbox's answer to a confirm dialog
 * (01-roles.md §1.1, no rubber-stamp prompts). An action toast stays up for
 * {@link GROOMING_ACTION_TOAST_MS} so there is time to press it, and pressing
 * it dismisses the toast.
 */

export const GROOMING_TOAST_LIMIT = 3;
export const GROOMING_TOAST_MS = 3200;
/** How long a toast with an action (Undo) stays up. */
export const GROOMING_ACTION_TOAST_MS = 8000;

/** The toast's icon: a check for done, info for a hint, a bell for a notification. */
export type GroomingToastKind = "success" | "info" | "notice";

/** One button on a toast, such as Undo. */
export interface GroomingToastAction {
  /** The button's words, e.g. "Undo". */
  label: string;
  /** Called once when the button is pressed; the toast is dismissed first. */
  onAction: () => void;
}

export interface GroomingToast {
  id: number;
  message: string;
  kind: GroomingToastKind;
  /** Present only on a toast shown with an action. */
  action?: GroomingToastAction;
}

export interface GroomingToastOptions {
  /** A button on the toast (Undo). The toast then stays up for {@link GROOMING_ACTION_TOAST_MS}. */
  action?: GroomingToastAction;
  /** Override how long the toast stays up, in milliseconds. */
  durationMs?: number;
}

interface GroomingToastState {
  toasts: GroomingToast[];
  dismiss: (id: number) => void;
}

let nextToastId = 0;

export const useGroomingToastStore = create<GroomingToastState>((set) => ({
  toasts: [],
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

/** Show a grooming toast, optionally with one action (Undo). Returns its id. */
export function showGroomingToast(
  message: string,
  kind: GroomingToastKind = "success",
  options: GroomingToastOptions = {}
): number {
  nextToastId += 1;
  const id = nextToastId;
  const toast: GroomingToast = options.action ? { id, message, kind, action: options.action } : { id, message, kind };
  useGroomingToastStore.setState((state) => ({
    toasts: [...state.toasts, toast].slice(-GROOMING_TOAST_LIMIT),
  }));
  const duration = options.durationMs ?? (options.action ? GROOMING_ACTION_TOAST_MS : GROOMING_TOAST_MS);
  setTimeout(() => {
    useGroomingToastStore.getState().dismiss(id);
  }, duration);
  return id;
}

/** Press a toast's action: dismiss the toast, then run the action once. A toast already gone does nothing. */
export function runGroomingToastAction(id: number): void {
  const toast = useGroomingToastStore.getState().toasts.find((candidate) => candidate.id === id);
  if (!toast?.action) return;
  useGroomingToastStore.getState().dismiss(id);
  toast.action.onAction();
}
